import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faHeadset,
  faExclamationTriangle,
  faInfoCircle,
  faIdCard,
  faPhone,
  faUser,
  faShieldAlt,
  faCheckCircle,
  faPhoneVolume,
} from "@fortawesome/free-solid-svg-icons";
import { IoArrowBack } from "react-icons/io5";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  Container,
  InputGroup,
  Modal,
  Badge,
} from "@themesberg/react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { Routes } from "../../routes";

// Replace this with your actual logo asset
// import BrandLogo from "../../assets/img/brand/brandlogo.jpg";
import BrandLogo from "../../assets/img/brand/Brand_Logo.png";
import {
  register,
  validateUser,
  sendKycOtp,
  verifyKycOtp,
  verifyPanKyc
} from "../api";

const REFERRAL_VALIDATE_API_URL = "/api/auth/referral/validate";
const AADHAAR_START_API_URL = "/api/auth/aadhaar/start";
const AADHAAR_VERIFY_OTP_API_URL = "/api/auth/aadhaar/verify-otp";
const REGISTER_API_URL = "/api/auth/register";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRegex = /^[6-9]\d{9}$/;
const otpRegex = /^\d{6}$/;
const aadhaarRegex = /^\d{12}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const panDobRegex =
  /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/([0-9]{4})$/;

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getLoginPath = () => {
  return Routes?.SignIn?.path || "/sign-in";
};

const getSupportPath = () => {
  return Routes?.Support?.path || "#";
};

const maskMobile = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 4) return "XXXXXX";
  return `XXXXXX${digits.slice(-4)}`;
};

const getKycBadgeVariant = (status) => {
  switch (status) {
    case "VERIFIED":
      return "success";
    case "FAILED":
      return "danger";
    case "OTP_SENT":
      return "warning";
    default:
      return "secondary";
  }
};

const getEmailError = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "Email ID is required.";
  if (!emailRegex.test(trimmed)) return "Please enter a valid email address.";
  return "";
};

const getPasswordRules = (password) => {
  const value = String(password || "");
  return {
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
    special: /[@$!%*?&]/.test(value),
    minLength: value.length >= 8,
  };
};

const isPasswordValid = (password) => {
  const rules = getPasswordRules(password);
  return Object.values(rules).every(Boolean);
};

const getConfirmPasswordError = (password, confirmPassword) => {
  const confirm = String(confirmPassword || "");
  if (!confirm) return "Confirm password is required.";
  if (password !== confirm) return "Passwords do not match.";
  return "";
};

export default function Register() {
  const navigate = useNavigate();

  const location = useLocation();

  const [formData, setFormData] = useState({
    referralNumber: "",
    fullName: "",
    mobileNumber: "",
    emailId: "",
    password: "",
    confirmPassword: "",
    referred_by: "",
    termsAccepted: false,
    privacyAccepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [referrerName, setReferrerName] = useState("");
  const [referrerUserId, setUserReferrerId] = useState("");
  const [referralValidated, setReferralValidated] = useState(false);

  const [aadhaarStatus, setAadhaarStatus] = useState("PENDING");
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [aadhaarSessionId, setAadhaarSessionId] = useState("");
  const [maskedMobileNumber, setMaskedMobileNumber] = useState("");
  const [verifiedName, setVerifiedName] = useState("");
  const [aadhaarOtp, setAadhaarOtp] = useState("");
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

  const [activeModal, setActiveModal] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  const [showKycForm, setShowKycForm] = useState(false);

  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [kycOtp, setKycOtp] = useState("");
  const [kycId, setKycId] = useState("");

  const [kycOtpSent, setKycOtpSent] = useState(false);
  const [kycVerified, setKycVerified] = useState(false);
  const [kycStatus, setKycStatus] = useState("PENDING");
  const [kycOtpTimer, setKycOtpTimer] = useState(0);

  // ===== PAN KYC STATES =====
  const [showPanForm, setShowPanForm] = useState(false);

  const [panNumber, setPanNumber] = useState("");
  const [panName, setPanName] = useState("");
  const [panDob, setPanDob] = useState("");

  const [panOtp, setPanOtp] = useState("");
  const [panOtpSent, setPanOtpSent] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [panKycId, setPanKycId] = useState("");
  const [panOtpTimer, setPanOtpTimer] = useState(0);

  // ===== FORM VISIBILITY =====
  const [showAadhaarForm, setShowAadhaarForm] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referral = params.get("referral_number");

    if (!referral) return;

    setFormData((prev) => ({
      ...prev,
      referralNumber: referral,
    }));
  }, [location.search]);


  //   useEffect(() => {
  //   if (!trimmedReferralNumber || referralValidated) return;

  //   const timer = setTimeout(() => {
  //     validateReferral();
  //   }, 300);

  //   return () => clearTimeout(timer);
  // }, [trimmedReferralNumber]);


  const trimmedReferralNumber = useMemo(
    () => formData.referralNumber.trim(),
    [formData.referralNumber]
  );
  const trimmedFullName = useMemo(() => formData.fullName.trim(), [formData.fullName]);
  const trimmedMobileNumber = useMemo(
    () => formData.mobileNumber.trim(),
    [formData.mobileNumber]
  );
  const trimmedEmailId = useMemo(() => formData.emailId.trim(), [formData.emailId]);

  useEffect(() => {
    if (!aadhaarOtpSent || aadhaarVerified || otpSecondsLeft <= 0) return;

    const timer = setInterval(() => {
      setOtpSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [aadhaarOtpSent, aadhaarVerified, otpSecondsLeft]);


  useEffect(() => {
    if (kycOtpTimer <= 0) return;

    const timer = setInterval(() => {
      setKycOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [kycOtpTimer]);


  const openModal = (type, message = "") => {
    setActiveModal(type);
    setModalMessage(message);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalMessage("");
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const nextForm = {
        ...prev,
        [name]: nextValue,
      };

      if (name === "emailId") {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          emailId: getEmailError(nextValue),
        }));
      }

      if (name === "password") {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          password: nextValue.trim()
            ? isPasswordValid(nextValue)
              ? ""
              : "Password must meet all the rules below."
            : "Password is required.",
          confirmPassword: nextForm.confirmPassword
            ? getConfirmPasswordError(nextValue, nextForm.confirmPassword)
            : prevErrors.confirmPassword,
        }));
      }

      if (name === "confirmPassword") {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          confirmPassword: getConfirmPasswordError(nextForm.password, nextValue),
        }));
      }

      if (type === "checkbox") {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "",
        }));
      }

      return nextForm;
    });
  };

  const validateReferral = async () => {
    if (!trimmedReferralNumber) {
      setFieldErrors((prev) => ({
        ...prev,
        referralNumber: "Referral number is required.",
      }));
      return;
    }

    try {
      setSubmitting(true);

      const response = await validateUser({
        referral_number: trimmedReferralNumber,
      });

      // console.log(response);

      const responseData = response?.data || {};
      const validatedUser = responseData?.data || {};

      if (!responseData?.success || !validatedUser?.name) {
        setReferralValidated(false);
        setReferrerName("");
        setUserReferrerId("");
        openModal(
          "invalidReferral",
          responseData?.message || "Referral number is not active or does not exist."
        );
        return;
      }

      setReferralValidated(true);
      setReferrerName(validatedUser?.name || "");
      setUserReferrerId(validatedUser?.id || "");
      setFieldErrors((prev) => ({
        ...prev,
        referralNumber: "",
      }));
    } catch (error) {
      setReferralValidated(false);
      setReferrerName("");
      setUserReferrerId("");

      const message =
        error?.response?.data?.message ||
        "Unable to validate referral number. Please try again.";

      openModal("serverError", message);
    } finally {
      setSubmitting(false);
    }
  };

  const startAadhaarVerification = async () => {
    if (!referralValidated) {
      openModal("invalidReferral", "Please validate a valid referral number first.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(AADHAAR_START_API_URL, {
        referralNumber: trimmedReferralNumber,
      });

      const data = response?.data || {};

      setAadhaarStatus("OTP_SENT");
      setAadhaarOtpSent(true);
      setAadhaarVerified(false);
      setAadhaarSessionId(data?.sessionId || "");
      setMaskedMobileNumber(data?.maskedMobile || "");
      setVerifiedName(data?.verifiedName || "");
      setOtpSecondsLeft(Number(data?.otpValiditySeconds || 300));
      setAadhaarOtp("");

      if (data?.verifiedName && !formData.fullName.trim()) {
        setFormData((prev) => ({
          ...prev,
          fullName: data.verifiedName,
        }));
      }

      openModal("info", "OTP has been sent to the Aadhaar-registered mobile number.");
    } catch (error) {
      setAadhaarStatus("FAILED");
      setAadhaarOtpSent(false);
      setAadhaarVerified(false);
      setAadhaarSessionId("");
      setMaskedMobileNumber("");
      setVerifiedName("");
      setOtpSecondsLeft(0);

      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "Aadhaar verification could not be started.";

      if (status === 429) {
        openModal("tooManyAttempts", message);
      } else {
        openModal("aadhaarFailed", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const verifyAadhaarOtp = async () => {
    if (!aadhaarOtpSent) {
      openModal("aadhaarFailed", "Please initiate Aadhaar verification first.");
      return;
    }

    if (!otpRegex.test(aadhaarOtp.trim())) {
      setFieldErrors((prev) => ({
        ...prev,
        aadhaarOtp: "Enter a valid 6-digit OTP.",
      }));
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(AADHAAR_VERIFY_OTP_API_URL, {
        sessionId: aadhaarSessionId,
        otp: aadhaarOtp.trim(),
      });

      const data = response?.data || {};

      setAadhaarStatus("VERIFIED");
      setAadhaarVerified(true);
      setAadhaarOtpSent(false);
      setOtpSecondsLeft(0);
      setFieldErrors((prev) => ({
        ...prev,
        aadhaarOtp: "",
      }));

      if (data?.verifiedName) {
        setVerifiedName(data.verifiedName);
        setFormData((prev) => ({
          ...prev,
          fullName: prev.fullName.trim() ? prev.fullName : data.verifiedName,
        }));
      }

      openModal("success", "Aadhaar KYC verified successfully.");
    } catch (error) {
      setAadhaarStatus("FAILED");
      setAadhaarVerified(false);

      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "OTP verification failed. Please try again.";

      if (status === 401 || status === 400) {
        openModal("aadhaarFailed", message);
      } else if (status === 429) {
        openModal("tooManyAttempts", message);
      } else {
        openModal("serverError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendKycOtp = async () => {
    if (!aadhaarRegex.test(aadhaarNumber.trim())) {
      setFieldErrors((prev) => ({
        ...prev,
        aadhaarNumber: "Enter valid 12 digit Aadhaar number.",
      }));
      return;
    }

    try {
      setSubmitting(false);

      // console.log("Sending KYC OTP for Aadhaar number:", aadhaarNumber.trim());
      // setKycOtpSent(true);
      // setKycId(1);
      // setKycStatus("OTP_SENT");

      // openModal(
      //   "success",
      //   data?.message || "OTP sent successfully."
      // );
      // return;

      const response = await sendKycOtp({
        aadhaar_number: aadhaarNumber.trim(),
      });

      const data = response?.data || {};

      if (data?.success) {
        setKycOtpSent(true);
        setKycId(data?.kyc_id);
        setKycStatus("OTP_SENT");
        setKycOtpTimer(30);

        openModal(
          "success",
          data?.message || "OTP sent successfully."
        );
      }
    } catch (error) {
      openModal(
        "serverError",
        error?.response?.data?.message ||
        "Unable to send OTP."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyKycOtp = async () => {
    if (!otpRegex.test(kycOtp.trim())) {
      setFieldErrors((prev) => ({
        ...prev,
        kycOtp: "Enter valid 6 digit OTP.",
      }));
      return;
    }

    try {
      setSubmitting(false);

      // console.log("Verifying KYC OTP for KYC ID:", kycId, "with OTP:", kycOtp.trim());
      // return;

      const response = await verifyKycOtp({
        kyc_id: kycId,
        otp: kycOtp.trim(),
      });

      const data = response?.data || {};

      if (data?.success) {
        setKycVerified(true);
        setKycStatus("VERIFIED");

        openModal(
          "success",
          data?.message || "OTP verified successfully."
        );
      } else {
        openModal("serverError", data?.message || "OTP verification failed.");
      }
    } catch (error) {
      setKycVerified(false);
      setKycStatus("FAILED");

      openModal(
        "serverError",
        error?.response?.data?.message ||
        "OTP verification failed."
      );
    } finally {
      setSubmitting(false);
    }
  };


  const handleVerifyPanKyc = async () => {
    if (!panRegex.test(panNumber.trim())) {
      setFieldErrors((prev) => ({
        ...prev,
        panNumber: "Enter valid PAN number",
      }));
      return;
    }

    if (!panName.trim()) {
      setFieldErrors((prev) => ({
        ...prev,
        panName: "Name as per PAN is required",
      }));
      return;
    }

    if (!panDobRegex.test(panDob.trim())) {
      setFieldErrors((prev) => ({
        ...prev,
        panDob: "DOB must be in DD/MM/YYYY format",
      }));
      return;
    }

    try {
      setSubmitting(true);

      const response = await verifyPanKyc({
        kyc_id: kycId, // Aadhaar kyc_id
        pan: panNumber.trim(),
        name_as_per_pan: panName.trim(),
        date_of_birth: panDob.trim(),
      });

      const data = response?.data || {};

      if (data?.success) {
        setPanKycId(data?.kyc_id);

        setPanVerified(true);

        setPanOtpSent(false);

        openModal(
          "success",
          data?.message || "PAN KYC verified successfully."
        );
      } else {
        openModal("serverError", data?.message || "PAN KYC verification failed.");
      }
    } catch (error) {
      setPanVerified(false);
      openModal(
        "serverError",
        error?.response?.data?.message || "Unable to verify PAN KYC."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validateBeforeSubmit = () => {
    const errors = {};

    if (!trimmedReferralNumber) {
      errors.referralNumber = "Referral number is required.";
    }

    if (!referralValidated) {
      errors.referralNumber = "Please validate the referral number first.";
    }

    if (!trimmedFullName) {
      errors.fullName = "Full name is required.";
    }

    if (!trimmedMobileNumber) {
      errors.mobileNumber = "Mobile number is required.";
    } else if (!mobileRegex.test(trimmedMobileNumber)) {
      errors.mobileNumber = "Enter a valid 10-digit mobile number.";
    }

    if (!trimmedEmailId) {
      errors.emailId = "Email ID is required.";
    } else if (!emailRegex.test(trimmedEmailId)) {
      errors.emailId = "Please enter a valid email address.";
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required.";
    } else if (!isPasswordValid(formData.password)) {
      errors.password = "Password must meet all the rules below.";
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = "Confirm password is required.";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    if (!formData.termsAccepted) {
      errors.termsAccepted = "You must accept the terms and conditions.";
    }

    if (!formData.privacyAccepted) {
      errors.privacyAccepted = "You must accept the privacy policy.";
    }

    // if (aadhaarStatus !== "VERIFIED") {
    //   errors.aadhaarStatus = "Aadhaar KYC must be verified before registration.";
    // }

    if (showKycForm && !kycVerified) {
      errors.kycOtp = "Please complete KYC verification.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinueToKyc = () => {
    if (!validateBeforeSubmit()) return;

    setShowKycForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateBeforeSubmit()) return;

    try {
      setSubmitting(true);



      const payload = {
        referralNumber: trimmedReferralNumber,
        referrerName,
        name: trimmedFullName,
        verifiedName,
        mobile_number: trimmedMobileNumber,
        email: trimmedEmailId,
        password: formData.password,
        termsAccepted: formData.termsAccepted,
        privacyAccepted: formData.privacyAccepted,
        referred_by: referrerUserId,
        aadhaarStatus,
        aadhaarSessionId,
        kyc_id: kycId,
        // kyc_status: kycStatus,
      };

      // console.log("Submitting registration payload:", payload);
      // return;

      await register(payload);

      openModal("success", "Registration completed successfully.");
      // show success toast as well
      // toast.success("Registration completed successfully.", {
      //   autoClose: 3000,
      // });

      setTimeout(() => {
        navigate(getLoginPath());
      }, 1200);
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        "Registration failed. Please check the entered details and try again.";

      if (status === 409) {
        openModal("conflict", message);
      } else if (status === 429) {
        openModal("tooManyAttempts", message);
      } else if (status === 400) {
        openModal("validationError", message);
      } else {
        openModal("serverError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const otpExpired = aadhaarOtpSent && otpSecondsLeft <= 0 && !aadhaarVerified;
  const passwordRules = getPasswordRules(formData.password);

  const handlePanDobKeyDown = (e) => {
  if (e.key === "Backspace") {
    const value = panDob;

    if (value.endsWith("/")) {
      e.preventDefault();
      setPanDob(value.slice(0, -1));
    }
  }
};

  // const formatPanDob = (value) => {
  //   const digits = value.replace(/\D/g, "").slice(0, 8);

  //   if (digits.length === 0) return "";

  //   if (digits.length <= 2) {
  //     return digits.length === 2 ? `${digits}/` : digits;
  //   }

  //   if (digits.length <= 4) {
  //     return digits.length === 4
  //       ? `${digits.slice(0, 2)}/${digits.slice(2)}/`
  //       : `${digits.slice(0, 2)}/${digits.slice(2)}`;
  //   }

  //   return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  // };


  const formatPanDob = (value) => {
  let digits = value.replace(/\D/g, "").slice(0, 8);

  // Day
  if (digits.length >= 2) {
    let day = parseInt(digits.slice(0, 2), 10);

    if (day === 0) day = 1;
    if (day > 31) day = 31;

    digits =
      String(day).padStart(2, "0") +
      digits.slice(2);
  }

  // Month
  if (digits.length >= 4) {
    let month = parseInt(digits.slice(2, 4), 10);

    if (month === 0) month = 1;
    if (month > 12) month = 12;

    digits =
      digits.slice(0, 2) +
      String(month).padStart(2, "0") +
      digits.slice(4);
  }

  if (digits.length <= 2)
    return digits.length === 2 ? `${digits}/` : digits;

  if (digits.length <= 4)
    return digits.length === 4
      ? `${digits.slice(0, 2)}/${digits.slice(2)}/`
      : `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

  return (
    <main className="auth-shell">
      <section className="py-5">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={9} xl={9}>
              <Card className="border-0 auth-card">
                <Card.Body className="p-4 p-md-5">
                  <div className="auth-logo-wrap">
                    <img src={BrandLogo} alt="LifeSkill Global" />
                  </div>

                  {/* <div className="text-center mb-4">
                    <h4 className="fw-bold mb-0">Register</h4>
                  </div> */}

                  <Form onSubmit={handleSubmit} noValidate>
                    {!showKycForm && !showPanForm && (
                      <>

                        <div className="text-center mb-4">
                          <h4 className="fw-bold mb-0">Register</h4>
                        </div>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group controlId="referralNumber">
                              <Form.Label className="fw-semibold">
                                Referral Number <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup className={`flex-nowrap align-items-stretch ${fieldErrors.referralNumber ? "invalid-input-group" : ""
                                }`}>
                                <InputGroup.Text className="bg-white">
                                  <FontAwesomeIcon icon={faIdCard} />
                                </InputGroup.Text>
                                <Form.Control
                                  name="referralNumber"
                                  type="text"
                                  placeholder="Referral Number"
                                  value={formData.referralNumber}
                                  onChange={handleChange}
                                  onBlur={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      referralNumber: prev.referralNumber.trim(),
                                    }))
                                  }
                                  isInvalid={!!fieldErrors.referralNumber}
                                  className="py-2 border-start-0"
                                  style={{ boxShadow: "none" }}
                                />
                                <Button
                                  type="button"
                                  onClick={validateReferral}
                                  disabled={!trimmedReferralNumber || submitting}
                                  style={{
                                    minWidth: "140px",
                                    ...ACTIVE_BUTTON_STYLE,
                                  }}
                                >
                                  Validate
                                </Button>
                              </InputGroup>
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {fieldErrors.referralNumber}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Form.Group controlId="referrerName">
                              <Form.Label className="fw-semibold">Referrer Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={referrerName}
                                placeholder="Auto-fetched referrer name"
                                readOnly
                                disabled
                                className="py-2"
                                style={{ cursor: "not-allowed" }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>



                        {/* <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold mb-0">Aadhaar KYC</h6>
                      <Badge bg={getKycBadgeVariant(aadhaarStatus)}>
                        {aadhaarStatus === "OTP_SENT"
                          ? "Pending"
                          : aadhaarStatus === "VERIFIED"
                            ? "Verified"
                            : aadhaarStatus === "FAILED"
                              ? "Failed"
                              : "Pending"}
                      </Badge>
                    </div>

                    <Row>
                      <Col md={12} className="mb-3">
                        <Form.Group controlId="verifyAadhaar">
                          <Button
                            type="button"
                            className="w-100 py-2 fw-semibold"
                            onClick={startAadhaarVerification}
                            disabled={
                              !referralValidated || submitting || aadhaarStatus === "VERIFIED"
                            }
                            style={{
                              ...ACTIVE_BUTTON_STYLE,
                            }}
                          >
                            Verify Aadhaar
                          </Button>
                        </Form.Group>
                      </Col>
                    </Row>

                      <Row>
                        <Col md={6} className="mb-3">
                          <Form.Group controlId="maskedMobileNumber">
                            <Form.Label className="fw-semibold">Masked Mobile Number</Form.Label>
                            <InputGroup className="flex-nowrap">
                              <InputGroup.Text className="bg-white">
                                <FontAwesomeIcon icon={faPhoneVolume} />
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                value={maskedMobileNumber}
                                placeholder="Will appear after verification starts"
                                readOnly
                                className="py-2"
                                style={{ boxShadow: "none" }}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>

                      <Col md={6} className="mb-3">
                        <Form.Group controlId="verifiedName">
                          <Form.Label className="fw-semibold">Verified Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={verifiedName}
                            placeholder="Will appear after successful Aadhaar verification"
                            readOnly
                            className="py-2"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {aadhaarOtpSent && (
                      <Row>
                        <Col md={6} className="mb-3">
                          <Form.Group controlId="aadhaarOtp">
                            <Form.Label className="fw-semibold">Aadhaar OTP</Form.Label>
                            <InputGroup className="flex-nowrap">
                              <InputGroup.Text className="bg-white">
                                <FontAwesomeIcon icon={faShieldAlt} />
                              </InputGroup.Text>
                              <Form.Control
                                name="aadhaarOtp"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="Enter 6-digit OTP"
                                value={aadhaarOtp}
                                onChange={(e) => {
                                  const nextValue = e.target.value.replace(/\D/g, "").slice(0, 6);
                                  setAadhaarOtp(nextValue);
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    aadhaarOtp: "",
                                  }));
                                }}
                                isInvalid={!!fieldErrors.aadhaarOtp}
                                className="py-2"
                                style={{ boxShadow: "none" }}
                              />
                              <Button
                                type="button"
                                onClick={verifyAadhaarOtp}
                                disabled={submitting || otpExpired}
                                style={{
                                  minWidth: "140px",
                                  ...ACTIVE_BUTTON_STYLE,
                                }}
                              >
                                Verify OTP
                              </Button>
                            </InputGroup>
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {fieldErrors.aadhaarOtp}
                            </Form.Control.Feedback>
                            <div className="mt-2 small text-muted">
                              {otpExpired
                                ? "OTP expired. Please start Aadhaar verification again."
                                : `OTP valid for ${Math.floor(otpSecondsLeft / 60)
                                  .toString()
                                  .padStart(2, "0")}:${(otpSecondsLeft % 60)
                                    .toString()
                                    .padStart(2, "0")}`}
                            </div>
                          </Form.Group>
                        </Col>

                        <Col md={6} className="mb-3 d-flex align-items-end">
                          <div className="w-100">
                            <div className="form-control bg-light">
                              OTP sent to Aadhaar registered mobile number
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )} */}


                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group controlId="fullName">
                              <Form.Label className="fw-semibold">
                                Full Name (as per Aadhaar) <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup
                                className={`flex-nowrap align-items-stretch ${fieldErrors.fullName ? "invalid-input-group" : ""
                                  }`}
                              >
                                <InputGroup.Text className="bg-white">
                                  <FontAwesomeIcon icon={faUser} />
                                </InputGroup.Text>
                                <Form.Control
                                  name="fullName"
                                  type="text"
                                  placeholder="Enter Full Name"
                                  value={formData.fullName}
                                  onChange={handleChange}
                                  onBlur={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      fullName: prev.fullName.trim(),
                                    }))
                                  }
                                  isInvalid={!!fieldErrors.fullName}
                                  className="py-2 border-start-0"
                                  style={{ boxShadow: "none" }}
                                />
                              </InputGroup>
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {fieldErrors.fullName}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Form.Group controlId="mobileNumber">
                              <Form.Label className="fw-semibold">
                                Mobile Number (as per Aadhaar) <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup className={`flex-nowrap align-items-stretch ${fieldErrors.mobileNumber ? "invalid-input-group" : ""
                                }`}>
                                <InputGroup.Text className="bg-white">
                                  <FontAwesomeIcon icon={faPhoneVolume} />
                                </InputGroup.Text>
                                <Form.Control
                                  name="mobileNumber"
                                  type="tel"
                                  inputMode="numeric"
                                  maxLength={10}
                                  placeholder="Enter Mobile Number"
                                  value={formData.mobileNumber}
                                  onChange={(e) => {
                                    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setFormData((prev) => ({
                                      ...prev,
                                      mobileNumber: nextValue,
                                    }));
                                    setFieldErrors((prev) => ({
                                      ...prev,
                                      mobileNumber: "",
                                    }));
                                  }}
                                  onBlur={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      mobileNumber: prev.mobileNumber.trim(),
                                    }))
                                  }
                                  isInvalid={!!fieldErrors.mobileNumber}
                                  className="py-2 border-start-0"
                                  style={{ boxShadow: "none" }}
                                />
                              </InputGroup>
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {fieldErrors.mobileNumber}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group controlId="emailId">
                              <Form.Label className="fw-semibold">
                                Email ID <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup
                                className={`flex-nowrap align-items-stretch ${fieldErrors.emailId ? "invalid-input-group" : ""
                                  }`}
                              >
                                <InputGroup.Text
                                  className="bg-white"
                                  style={{ minHeight: "46px" }}
                                >
                                  <FontAwesomeIcon icon={faEnvelope} />
                                </InputGroup.Text>
                                <Form.Control
                                  name="emailId"
                                  type="email"
                                  placeholder="Enter Email ID"
                                  value={formData.emailId}
                                  onChange={handleChange}
                                  onBlur={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      emailId: prev.emailId.trim(),
                                    }))
                                  }
                                  isInvalid={!!fieldErrors.emailId}
                                  autoComplete="email"
                                  className="py-2 border-start-0"
                                  style={{
                                    boxShadow: "none",
                                    minHeight: "46px",
                                  }}
                                />
                              </InputGroup>
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {fieldErrors.emailId}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Form.Group controlId="password">
                              <Form.Label className="fw-semibold">
                                Password <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup
                                className={`flex-nowrap align-items-stretch ${fieldErrors.password ? "invalid-input-group" : ""
                                  }`}
                              >
                                <InputGroup.Text
                                  className="bg-white"
                                  style={{ minHeight: "46px" }}
                                >
                                  <FontAwesomeIcon icon={faLock} />
                                </InputGroup.Text>

                                <Form.Control
                                  name="password"
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Enter Password"
                                  value={formData.password}
                                  onChange={handleChange}
                                  isInvalid={!!fieldErrors.password}
                                  autoComplete="new-password"
                                  className="py-2 border-start-0 border-end-0"
                                  style={{
                                    boxShadow: "none",
                                    minHeight: "46px",
                                  }}
                                />

                                <InputGroup.Text
                                  className="p-0"
                                  style={{
                                    background: "#e9ecef",
                                    minWidth: "58px",
                                    justifyContent: "center",
                                    borderColor: "#dee2e6",
                                    minHeight: "46px",
                                  }}
                                >
                                  <Button
                                    type="button"
                                    variant="link"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                                    style={{
                                      background: "transparent",
                                      color: "#5f6b7a",
                                      minHeight: "46px",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                  </Button>
                                </InputGroup.Text>
                              </InputGroup>
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {fieldErrors.password}
                              </Form.Control.Feedback>

                              {formData.password && (
                                <ul className="mt-2 mb-0 ps-4" style={{ color: "#dc3545" }}>
                                  {!passwordRules.uppercase && <li>At least 1 uppercase letter</li>}
                                  {!passwordRules.lowercase && <li>At least 1 lowercase letter</li>}
                                  {!passwordRules.number && <li>At least 1 number</li>}
                                  {!passwordRules.special && <li>At least 1 special character (@$!%*?&)</li>}
                                  {!passwordRules.minLength && <li>Minimum 8 characters</li>}
                                </ul>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group controlId="confirmPassword">
                              <Form.Label className="fw-semibold">
                                Confirm Password <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup
                                className={`flex-nowrap align-items-stretch ${fieldErrors.confirmPassword ? "invalid-input-group" : ""
                                  }`}
                              >
                                <InputGroup.Text
                                  className="bg-white"
                                  style={{ minHeight: "46px" }}
                                >
                                  <FontAwesomeIcon icon={faLock} />
                                </InputGroup.Text>

                                <Form.Control
                                  name="confirmPassword"
                                  type={showConfirmPassword ? "text" : "password"}
                                  placeholder="Re-enter Password"
                                  value={formData.confirmPassword}
                                  onChange={handleChange}
                                  isInvalid={!!fieldErrors.confirmPassword}
                                  autoComplete="new-password"
                                  className="py-2 border-start-0 border-end-0"
                                  style={{
                                    boxShadow: "none",
                                    minHeight: "46px",
                                  }}
                                />

                                <InputGroup.Text
                                  className="p-0"
                                  style={{
                                    background: "#e9ecef",
                                    minWidth: "58px",
                                    justifyContent: "center",
                                    borderColor: "#dee2e6",
                                    minHeight: "46px",
                                  }}
                                >
                                  <Button
                                    type="button"
                                    variant="link"
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    aria-label={
                                      showConfirmPassword ? "Hide password" : "Show password"
                                    }
                                    className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                                    style={{
                                      background: "transparent",
                                      color: "#5f6b7a",
                                      minHeight: "46px",
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={showConfirmPassword ? faEyeSlash : faEye}
                                    />
                                  </Button>
                                </InputGroup.Text>
                              </InputGroup>
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {fieldErrors.confirmPassword}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Check
                              type="checkbox"
                              id="termsAccepted"
                              name="termsAccepted"
                              checked={formData.termsAccepted}
                              onChange={handleChange}
                              label={
                                <>
                                  I accept the{" "}
                                  <a
                                    href="https://landing.lifeskillglobal.online/policies/terms-and-conditions.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="policy-link"
                                  >
                                    Terms & Conditions
                                  </a>{" "}
                                  <span className="text-danger">*</span>
                                </>
                              }
                            // isInvalid={!!fieldErrors.termsAccepted}
                            />
                            {fieldErrors.termsAccepted ? (
                              <div className="text-danger small mt-1">
                                {fieldErrors.termsAccepted}
                              </div>
                            ) : null}
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Check
                              type="checkbox"
                              id="privacyAccepted"
                              name="privacyAccepted"
                              checked={formData.privacyAccepted}
                              onChange={handleChange}
                              label={
                                <>
                                  I accept the{" "}
                                  <a
                                    href="https://landing.lifeskillglobal.online/policies/privacy-policy.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="policy-link"
                                  >
                                    Privacy Policy
                                  </a>{" "}
                                  <span className="text-danger">*</span>
                                </>
                              }
                            // isInvalid={!!fieldErrors.privacyAccepted}
                            />
                            {fieldErrors.privacyAccepted ? (
                              <div className="text-danger small mt-1">
                                {fieldErrors.privacyAccepted}
                              </div>
                            ) : null}
                          </Col>
                        </Row>

                      </>
                    )}


                    {showKycForm && (
                      <>
                        {/* <hr className="my-4" /> */}

                        <div className="text-center mb-4">
                          <h4 className="fw-bold mb-0">Aadhaar KYC Verification</h4>
                        </div>


                        {kycVerified && (
                          <Row>
                            <Col md={12} className="mb-3">
                              <div
                                className="p-2 rounded"
                                style={{
                                  background: "#d1e7dd",
                                  color: "#0f5132",
                                  border: "1px solid #badbcc",
                                }}
                              >
                                KYC verified successfully.
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Row>
                          <Col md={12} className="mb-3">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Aadhaar Number <span className="text-danger">*</span>
                              </Form.Label>

                              <div className="kyc-input-group">
                                <InputGroup
                                  className={`flex-nowrap align-items-stretch ${fieldErrors.aadhaarNumber ? "invalid-input-group" : ""
                                    }`}
                                >
                                  <InputGroup.Text className="bg-white">
                                    <FontAwesomeIcon icon={faIdCard} />
                                  </InputGroup.Text>

                                  <Form.Control
                                    type="text"
                                    maxLength={12}
                                    placeholder="Enter Aadhaar Number"
                                    value={aadhaarNumber}
                                    onChange={(e) => {
                                      const value = e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 12);

                                      setAadhaarNumber(value);

                                      setFieldErrors((prev) => ({
                                        ...prev,
                                        aadhaarNumber: "",
                                      }));
                                    }}
                                    className="py-2 border-start-0"
                                  />
                                </InputGroup>

                                {!kycVerified && (
                                  <Button
                                    type="button"
                                    className="kyc-action-btn"
                                    onClick={handleSendKycOtp}
                                    disabled={
                                      submitting ||
                                      aadhaarNumber.length !== 12 ||
                                      kycOtpTimer > 0
                                    }
                                    style={ACTIVE_BUTTON_STYLE}
                                  >
                                    {kycOtpTimer > 0
                                      ? `RESEND IN ${kycOtpTimer}s`
                                      : "SEND OTP"}
                                  </Button>
                                )}
                              </div>

                              <Form.Control.Feedback
                                type="invalid"
                                className="d-block"
                              >
                                {fieldErrors.aadhaarNumber}
                              </Form.Control.Feedback>

                              {kycOtpSent && !kycVerified && (
                                <div
                                  className="small mt-2"
                                  style={{
                                    color: "#6c757d",
                                  }}
                                >
                                  {kycOtpTimer > 0 ? (
                                    <>
                                      Resend OTP in{" "}
                                      <strong>{kycOtpTimer}</strong> sec
                                    </>
                                  ) : (
                                    <>
                                      Didn't receive OTP?{" "}
                                      <span
                                        role="button"
                                        onClick={handleSendKycOtp}
                                        style={{
                                          color: "#0d6efd",
                                          cursor: "pointer",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Resend OTP
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        {kycOtpSent && (
                          <Row>
                            <Col md={12} className="mb-3">
                              <Form.Group>
                                <Form.Label className="fw-semibold">
                                  Verify OTP
                                </Form.Label>

                                <div className="kyc-input-group">
                                  <InputGroup className="flex-nowrap">
                                    <InputGroup.Text className="bg-white">
                                      <FontAwesomeIcon icon={faShieldAlt} />
                                    </InputGroup.Text>

                                    <Form.Control
                                      type="text"
                                      maxLength={6}
                                      placeholder="Enter OTP"
                                      value={kycOtp}
                                      onChange={(e) => {
                                        const value = e.target.value
                                          .replace(/\D/g, "")
                                          .slice(0, 6);

                                        setKycOtp(value);

                                        setFieldErrors((prev) => ({
                                          ...prev,
                                          kycOtp: "",
                                        }));
                                      }}
                                    />
                                  </InputGroup>

                                  <Button
                                    type="button"
                                    className="kyc-action-btn"
                                    onClick={handleVerifyKycOtp}
                                    disabled={
                                      submitting ||
                                      kycOtp.length !== 6
                                    }
                                    style={ACTIVE_BUTTON_STYLE}
                                  >
                                    VERIFY OTP
                                  </Button>
                                </div>

                                <Form.Control.Feedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {fieldErrors.kycOtp}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}


                        {/* <Button
                          type="button"
                          className="mb-3"
                          onClick={() => setShowKycForm(false)}
                        >
                          <IoArrowBack className="me-2" />
                          Back
                        </Button> */}

                        <Link
                          // to={getPasswordLoginPath()}
                          onClick={() => {
                            setShowKycForm(false);
                            setShowPanForm(false);
                          }}
                          className="fw-semibold underline-link"
                          style={{ color: "#1D4E89" }}
                        >
                          <IoArrowBack />
                          Back
                        </Link>

                      </>
                    )}


                    {showPanForm && (
                      <>
                        {/* <hr className="my-4" /> */}

                        <div className="text-center mb-4">
                          <h4 className="fw-bold mb-0">Pan KYC Verification</h4>
                        </div>

                        <Row>
                          <Col md={4} className="mb-3">
                            <Form.Label>PAN Number <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              value={panNumber}
                              placeholder="ABCDE1234F"
                              // onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                              onChange={(e) => {
                                const value = e.target.value
                                  .toUpperCase()
                                  .replace(/[^A-Z0-9]/g, "")
                                  .slice(0, 10);

                                setPanNumber(value);

                                setFieldErrors((prev) => ({
                                  ...prev,
                                  panNumber: "",
                                }));
                              }}
                            />
                          </Col>

                          <Col md={4} className="mb-3">
                            <Form.Label>Name as per PAN <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              placeholder="John Doe"
                              value={panName}
                              onChange={(e) => setPanName(e.target.value)}
                            />
                          </Col>

                          <Col md={4} className="mb-3">
                            <Form.Label>DOB (DD/MM/YYYY) <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              value={panDob}
                              placeholder="DD/MM/YYYY"
                              // onChange={(e) => setPanDob(e.target.value)}
                              onKeyDown={handlePanDobKeyDown}
                              onChange={(e) => {
                                setPanDob(formatPanDob(e.target.value));

                                setFieldErrors((prev) => ({
                                  ...prev,
                                  panDob: "",
                                }));
                              }}
                            />
                          </Col>
                        </Row>

                        <Link
                          // to={getPasswordLoginPath()}
                          onClick={() => {
                            setShowKycForm(true);
                            setShowPanForm(false);
                          }}
                          className="fw-semibold underline-link"
                          style={{ color: "#1D4E89" }}
                        >
                          <IoArrowBack />
                          Back
                        </Link>

                        {/* SEND / RESEND OTP */}
                        {!panVerified && (
                          <div className="text-center mt-3">
                            <Button
                              type="button"
                              onClick={handleVerifyPanKyc}
                              disabled={
                                submitting ||
                                !panNumber.trim() ||
                                !panName.trim() ||
                                !panDob.trim() ||
                                !panRegex.test(panNumber.trim()) ||
                                !panDobRegex.test(panDob.trim())
                              }
                              style={{
                                minWidth: "220px",
                                ...ACTIVE_BUTTON_STYLE,
                              }}
                            >
                              {submitting ? "VERIFYING..." : "VERIFY PAN"}
                            </Button>
                          </div>
                        )}

                      </>
                    )}


                    <Row className="justify-content-center">
                      <Col md={6} className="mb-3 d-flex justify-content-center">
                        {!showKycForm && !showPanForm ? (
                          <Button
                            type="button"
                            disabled={submitting}
                            onClick={handleContinueToKyc}
                            className="py-2 fw-bold"
                            style={{
                              minWidth: "180px",
                              ...ACTIVE_BUTTON_STYLE,
                            }}
                          >
                            CONTINUE TO KYC
                          </Button>
                        ) :
                          showKycForm && !showPanForm ? (
                            <Button
                              type="button"
                              className="fw-bold"
                              disabled={submitting || !kycVerified}
                              style={{ minWidth: "260px", ...ACTIVE_BUTTON_STYLE }}
                              onClick={() => {
                                setShowPanForm(true);
                                setShowKycForm(false);
                              }}
                            >
                              CONTINUE TO PAN VERIFICATION
                            </Button>
                          ) :
                            (
                              <Button
                                type="submit"
                                disabled={submitting || !kycVerified || !panVerified}
                                className="py-2 fw-bold mt-3"
                                style={{
                                  minWidth: "180px",
                                  ...ACTIVE_BUTTON_STYLE,
                                }}
                              >
                                {submitting ? "REGISTERING..." : "REGISTER"}
                              </Button>
                            )}
                      </Col>
                    </Row>

                    <div className="text-center mt-4">
                      <span className="text-dark">Already have an account? </span>
                      <Link
                        to={getLoginPath()}
                        className="fw-bold underline-link"
                        style={{ color: "#1D4E89" }}
                      >
                        Sign in
                      </Link>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="justify-content-center mt-4 mt-md-5">
            <Col xs={12} md={10} lg={9} xl={9}>
              <Card className="border-0 shadow-sm rounded-4" style={{ background: "#ffffff" }}>
                <Card.Body className="p-4 p-md-5">
                  <h4 className="fw-bold mb-2">Need help?</h4>
                  <p className="mb-0 text-muted">
                    Contact your administrator if you cannot complete registration.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Modal show={activeModal === "invalidReferral"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Invalid Referral
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Referral number is invalid or inactive."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Try Again
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "aadhaarFailed"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Aadhaar Verification Failed
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Aadhaar verification could not be completed."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
          <Button className="act-btn" onClick={startAadhaarVerification}>
            Try Again
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "tooManyAttempts"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Too Many Attempts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Too many failed attempts detected. Please try again later."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "validationError"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
            Validation Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Please check the entered details and try again."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "conflict"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
            Already Registered
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "This mobile number or email ID is already registered."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
          <Button className="act-btn" as={Link} to={getLoginPath()} onClick={closeModal}>
            Sign in
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "serverError"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Server / Network Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Unable to reach the server. Please try again later."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Try Again
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "info"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
            Information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "success"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
            Success
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={supportModalOpen} onHide={() => setSupportModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHeadset} className="me-2" />
            Contact Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Use your support details here.</p>
          <p className="mb-0 text-muted">
            Example: support@yourcompany.com | +91-XXXXXXXXXX
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setSupportModalOpen(false)}>
            Close
          </Button>
          <Button
            className="act-btn"
            as={Link}
            to={getSupportPath()}
            onClick={() => setSupportModalOpen(false)}
          >
            Open Support
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
      .otp-login-btn,
.otp-login-btn:hover,
.otp-login-btn:focus,
.otp-login-btn:active {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: #ffffff !important;
}

.act-btn {
  background: #1D4E89;
  border: none !important;
  border-radius: 0.5rem;
  color: #ffffff !important;
}

.invalid-input-group .form-control,
.invalid-input-group .input-group-text {
  border-color: #dc3545 !important;
}

.invalid-input-group .input-group-text:last-child {
  border-right: 1px solid #dc3545 !important;
}

.invalid-input-group .form-control:focus {
  border-color: #dc3545 !important;
  box-shadow: none !important;
}
  

/* ---------- Responsive KYC Input ---------- */

.kyc-input-group {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
}

.kyc-action-btn {
  min-width: 150px;
}

@media (max-width: 576px) {
  .kyc-input-group {
    display: block;
  }

  .kyc-input-group .input-group {
    width: 100%;
  }

  .kyc-action-btn {
    width: 100%;
    margin-top: 10px;
    min-width: 100%;
  }

  .kyc-input-group .form-control {
    font-size: 0.9rem;
  }

  .kyc-input-group .form-control::placeholder {
    font-size: 0.85rem;
  }
}

.policy-link {
  color: #1D4E89;
  text-decoration: none;
  transition: text-decoration 0.2s ease;
}

.policy-link:hover {
  color: #1D4E89;
  text-decoration: underline;
}

.policy-link:focus,
.policy-link:active,
.policy-link:visited {
  color: #1D4E89;
}

`}
      </style>
    </main>
  );
}
